import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import financeService from '../services/financeService';
import { useAuth } from '../contexts/AuthContext';
import { hasPermission } from '../config/permissions';
import toast from 'react-hot-toast';
import { 
  Settings, Save, Edit, Info, AlertCircle, CheckCircle, XCircle 
} from 'lucide-react';

const PayrollIndicatorsConfig = () => {
  const { user } = useAuth();
  const canEdit = hasPermission(user?.role, 'financial:full');
  const queryClient = useQueryClient();
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});

  const { data: indicatorsData, isLoading } = useQuery({
    queryKey: ['payroll-indicators'],
    queryFn: () => financeService.getPayrollIndicators(),
    staleTime: 5 * 60 * 1000,
  });

  const indicators = indicatorsData?.data || [];

  const updateMutation = useMutation({
    mutationFn: ({ id, payload }) => financeService.updatePayrollIndicator(id, payload),
    onSuccess: () => {
      toast.success('Configuração atualizada com sucesso!');
      queryClient.invalidateQueries(['payroll-indicators']);
      setEditingId(null);
      setEditForm({});
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Erro ao atualizar configuração');
    }
  });

  const handleEdit = (indicator) => {
    setEditingId(indicator.id);
    setEditForm({
      threshold_percentage: indicator.threshold_percentage,
      is_active: indicator.is_active,
      description: indicator.description
    });
  };

  const handleSave = (id) => {
    updateMutation.mutate({ id, payload: editForm });
  };

  const handleCancel = () => {
    setEditingId(null);
    setEditForm({});
  };

  if (!canEdit) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-600">Você não tem permissão para gerenciar configurações de indicadores.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Cabeçalho */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
            <Settings className="w-8 h-8" />
            Configuração de Indicadores de Folha
          </h1>
          <p className="text-gray-600 mt-2">
            Configure os parâmetros que determinam quando os alertas serão exibidos na análise de folha de pagamento
          </p>
        </div>
      </div>

      {/* Informação */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <Info className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
          <div className="text-sm text-blue-800">
            <p className="font-semibold mb-2">Como funcionam os Indicadores:</p>
            <ul className="list-disc list-inside space-y-1 ml-2">
              <li>Os indicadores comparam valores específicos (ex: horas extras) com o total de gastos efetivos</li>
              <li>Quando o percentual ultrapassa o limite configurado, um alerta vermelho é exibido</li>
              <li>Quando está abaixo do limite, um alerta amarelo (informativo) é exibido</li>
              <li>Os valores são calculados automaticamente com base nos holerites aprovados</li>
            </ul>
          </div>
        </div>
      </div>

      {isLoading ? (
        <div className="animate-pulse space-y-4">
          <div className="h-32 bg-gray-200 rounded"></div>
          <div className="h-32 bg-gray-200 rounded"></div>
        </div>
      ) : indicators.length === 0 ? (
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-8 text-center">
          <p className="text-gray-600">Nenhum indicador configurado ainda.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {indicators.map((indicator) => {
            const isEditing = editingId === indicator.id;

            return (
              <div
                key={indicator.id}
                className="bg-white rounded-lg shadow-md border border-gray-200 p-6"
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="text-lg font-bold text-gray-900">
                        {indicator.indicator_name}
                      </h3>
                      {indicator.is_active ? (
                        <span className="px-2 py-1 text-xs font-medium bg-green-100 text-green-800 rounded-full flex items-center gap-1">
                          <CheckCircle className="w-3 h-3" />
                          Ativo
                        </span>
                      ) : (
                        <span className="px-2 py-1 text-xs font-medium bg-gray-100 text-gray-800 rounded-full flex items-center gap-1">
                          <XCircle className="w-3 h-3" />
                          Inativo
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-gray-600 mb-4 leading-relaxed">
                      {indicator.description}
                    </p>
                  </div>
                  {canEdit && (
                    <button
                      onClick={() => isEditing ? handleCancel() : handleEdit(indicator)}
                      className="ml-4 px-4 py-2 text-sm font-medium text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-lg transition-colors flex items-center gap-2"
                    >
                      {isEditing ? (
                        <>
                          <XCircle className="w-4 h-4" />
                          Cancelar
                        </>
                      ) : (
                        <>
                          <Edit className="w-4 h-4" />
                          Editar
                        </>
                      )}
                    </button>
                  )}
                </div>

                {isEditing ? (
                  <div className="space-y-4 pt-4 border-t border-gray-200">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Percentual Limite (%)
                        <span className="text-red-500 ml-1">*</span>
                      </label>
                      <div className="flex items-center gap-4">
                        <input
                          type="number"
                          min="0"
                          max="100"
                          step="0.01"
                          value={editForm.threshold_percentage || ''}
                          onChange={(e) => setEditForm({
                            ...editForm,
                            threshold_percentage: parseFloat(e.target.value) || 0
                          })}
                          className="w-32 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          placeholder="10.00"
                        />
                        <span className="text-sm text-gray-600">
                          % do total de gastos efetivos
                        </span>
                      </div>
                      <p className="text-xs text-gray-500 mt-1">
                        Exemplo: Se configurado em 10%, quando as horas extras representarem mais de 10% do total de gastos efetivos, será exibido um alerta crítico.
                      </p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Descrição Detalhada
                      </label>
                      <textarea
                        value={editForm.description || ''}
                        onChange={(e) => setEditForm({
                          ...editForm,
                          description: e.target.value
                        })}
                        rows="3"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="Descrição detalhada do indicador..."
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Esta descrição será exibida para ajudar a entender o que significa este indicador.
                      </p>
                    </div>

                    <div className="flex items-center gap-4">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={editForm.is_active || false}
                          onChange={(e) => setEditForm({
                            ...editForm,
                            is_active: e.target.checked
                          })}
                          className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                        />
                        <span className="text-sm text-gray-700">Indicador ativo</span>
                      </label>
                    </div>

                    <div className="flex items-center gap-3 pt-4">
                      <button
                        onClick={() => handleSave(indicator.id)}
                        disabled={updateMutation.isLoading}
                        className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                      >
                        <Save className="w-4 h-4" />
                        {updateMutation.isLoading ? 'Salvando...' : 'Salvar Alterações'}
                      </button>
                      <button
                        onClick={handleCancel}
                        className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
                      >
                        Cancelar
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="pt-4 border-t border-gray-200">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="bg-gray-50 rounded-lg p-4">
                        <div className="flex items-center gap-2 mb-2">
                          <AlertCircle className="w-5 h-5 text-orange-500" />
                          <span className="text-sm font-medium text-gray-700">Limite Configurado</span>
                        </div>
                        <p className="text-2xl font-bold text-gray-900">
                          {Number(indicator.threshold_percentage).toFixed(2)}%
                        </p>
                        <p className="text-xs text-gray-500 mt-1">
                          Quando ultrapassar este valor, será exibido alerta crítico
                        </p>
                      </div>
                      <div className="bg-gray-50 rounded-lg p-4">
                        <div className="flex items-center gap-2 mb-2">
                          <Info className="w-5 h-5 text-blue-500" />
                          <span className="text-sm font-medium text-gray-700">Como é Calculado</span>
                        </div>
                        <p className="text-sm text-gray-700">
                          O sistema compara o valor específico (ex: horas extras) com o <strong>total de gastos efetivos</strong> do período analisado e calcula o percentual. Se este percentual for maior que o limite configurado, um alerta vermelho será exibido.
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default PayrollIndicatorsConfig;
